import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Row, Col, Switch, FormInstance, theme, Skeleton, Divider } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { useState } from 'react';

import { useStores } from '@/hooks/use-stores';
import { useResponsive } from '@/hooks/useResponsive';
import { EPSelect, EPSelectOption } from '@/components';
import { permissions } from '@/constants';
import CompressorWidget from '@/comp/common/icons/CompressorWidget';

import { Entity } from '../../../NodeWidget.types';
import { EditCompressorFormContainer } from './EditCompressorForm.styles';
import {
  getCompressorTypeOptions,
  isCentrifugalCompressor,
  isFixedSpeedCompressor,
  mapFormValuesToPayload,
  mapUnusedSerialNumbersToOptions,
  useInitialValues,
  useIsSerialNumberReadOnly,
} from './EditCompressorForm.utils';

import {
  DEFAULT_MODEL_IDS,
  DUMMY_MODEL_IDS,
  getDeprecatedModelToolTip,
  getFieldSelectProps,
  IsDeprecatedModel,
  IsDisconnectedDevice,
  useFormHelper,
} from '../../EditAssetDrawer.utils';
import { compressorTypes } from '@/constants/deviceTypeEnum';
import ConnectionList from '../ConnectionList/ConnectionList';
import ConnectionSelector from '../ConnectionSelector/ConnectionSelector';

const EditCompressorForm = observer(
  ({
    entity,
    form,
    afterOpenChange,
    messageApi,
  }: {
    entity: Entity;
    form: FormInstance;
    afterOpenChange: boolean;
    messageApi: MessageInstance;
  }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const { pidEditStore, permissionStore } = useStores();
    const { isMobile } = useResponsive();
    const [currentDeviceType, setCurrentDeviceType] = useState(entity.deviceType);

    const { deviceModelOptions, isLoadingModels, onFinish, onModelSearch, deviceConnectionOptions, initialValues } =
      useFormHelper(
        t,
        entity,
        afterOpenChange,
        messageApi,
        mapFormValuesToPayload,
        useInitialValues,
        currentDeviceType,
      );

    const initialSerialNumberOptions = mapUnusedSerialNumbersToOptions(pidEditStore.unusedSerialNumbers);
    const [serialNumberOptions, setSerialNumberOptions] =
      useState<{ label: string; value: string }[]>(initialSerialNumberOptions);

    const hasSerialNumber = !!initialValues.serialNumber;
    const isSerialNumberReadOnly = hasSerialNumber
      ? useIsSerialNumberReadOnly(initialValues.serialNumber as string)
      : false;

    const hasAdditionalFieldsPermission = permissionStore.isAuth(permissions.UPDATE_COMPRESSOR_ADDITIONAL_FIELDS);
    const isCentrifugal = isCentrifugalCompressor(currentDeviceType);
    const isFixedSpeed = isFixedSpeedCompressor(currentDeviceType);
    const deprecatedModel = IsDeprecatedModel(entity.deviceKind);

    const currentModelId = Form.useWatch('deviceModelId', form);
    const disconnectedDevice = IsDisconnectedDevice(deviceModelOptions, currentModelId);

    const isLoading = !afterOpenChange || pidEditStore.isLoadingDraft;

    const setNameField = () => {
      const values = form.getFieldsValue();
      const oldNameSecondPart = values.name ? values.name.split(' - ')[1] : '';
      const modelName = deviceModelOptions.find(option => option.value === values.deviceModelId)?.label;
      const newNameSecondPart =
        values.deviceModelId === DUMMY_MODEL_IDS.dummyCompressor || !modelName ? oldNameSecondPart : modelName;
      form.setFieldValue('name', `#${values.compressorNumber ?? ''} - ${newNameSecondPart}`);
    };

    const onCompressorNumberChange = e => {
      form.setFieldValue('compressorNumber', e.target.value);
      setNameField();
    };

    const onModelSpecChange = value => {
      form.setFieldValue('deviceModelId', value);
      setNameField();
    };

    const onSerialNumberSearch = async value => {
      setSerialNumberOptions([
        ...initialSerialNumberOptions.filter(option => option.label.toLowerCase().includes(value.toLowerCase())),
        ...(value ? [{ value, label: t('newSerial', { serialNumber: value }) }] : []),
      ]);
    };

    const getSerialNumberTooltip = () => {
      if (hasSerialNumber && isSerialNumberReadOnly) {
        return t('SerialImportedFromSalesforce');
      }
      return '';
    };

    const onSerialNumberChange = value => {
      form.setFieldValue('serialNumber', value);
      setSerialNumberOptions(mapUnusedSerialNumbersToOptions(pidEditStore.unusedSerialNumbers));
    };

    const onDeviceTypeChange = newDeviceType => {
      setCurrentDeviceType(newDeviceType);
      form.setFieldValue('deviceModelId', DEFAULT_MODEL_IDS.dummyCompressor);
    };

    if (isLoading) {
      return (
        <EditCompressorFormContainer>
          <Skeleton active paragraph={{ rows: 6 }} />
        </EditCompressorFormContainer>
      );
    }

    return (
      <EditCompressorFormContainer>
        <Form layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
          <span className="horizontal-label">{t('connect')}</span>
          <Row className="connected-to-row">
            <Col span={8} className="widget">
              <CompressorWidget width={125} height={100} color={token.colorBorder} />
            </Col>
            <Col span={8}>
              <div className="pipe" />
            </Col>
            <Col span={8}>
              <Form.Item label=" " name="connectTo">
                <ConnectionSelector
                  label="to"
                  options={deviceConnectionOptions.filter(option => !compressorTypes.includes(option.deviceType))}
                  form={form}
                />
              </Form.Item>
            </Col>
          </Row>
          <ConnectionList form={form} field="from" />
          <ConnectionList form={form} field="to" />
          <Divider className="divider" />
          <Row gutter={16}>
            <Col span={5}>
              <Form.Item label={t('number')} name="compressorNumber">
                <Input type="number" onChange={onCompressorNumberChange} size="large" />
              </Form.Item>
            </Col>
            <Col span={19}>
              <Form.Item label={t('name')} name="name">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t('serialNumber')} name="serialNumber" tooltip={getSerialNumberTooltip()}>
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectSerialNumber'), 'serialNumber')}
              placeholder={t('selectOrTypeNumber')}
              onSearch={onSerialNumberSearch}
              filterOption={false}
              disabled={hasSerialNumber && isSerialNumberReadOnly}
              onChange={onSerialNumberChange}
            >
              {serialNumberOptions.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
          {!entity?.deviceId && (
            <Form.Item label={t('type')} name="deviceType">
              <EPSelect
                {...getFieldSelectProps(isMobile, form, t('selectType'), 'deviceType')}
                options={getCompressorTypeOptions(t)}
                onChange={onDeviceTypeChange}
              />
            </Form.Item>
          )}
          <Form.Item
            label={t('modelSpec')}
            name="deviceModelId"
            tooltip={deprecatedModel ? getDeprecatedModelToolTip() : undefined}
            required
            rules={[{ required: true, message: t('required') }]}
          >
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectModelSpec'), 'deviceModelId')}
              loading={isLoadingModels}
              onChange={onModelSpecChange}
              onSearch={onModelSearch}
              filterOption={false}
              disabled={deprecatedModel}
            >
              {deviceModelOptions.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
          <Form.Item label={t('controller')} name="controllerId" rules={[{ required: !disconnectedDevice, message: t('required') }]}>
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectController'), 'controllerId')}
              disabled={disconnectedDevice || isLoadingModels}
            >
              {pidEditStore.deviceControllers.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
          {hasAdditionalFieldsPermission &&
            (isFixedSpeed ? (
              <div className="switch-container">
                <span className="horizontal-label">{t('bandsControl')}</span>
                <Form.Item name="bandsControl">
                  <Switch />
                </Form.Item>
              </div>
            ) : isCentrifugal ? (
              <div className="switch-container">
                <span className="horizontal-label">{t('five loop')}</span>
                <Form.Item name="fiveLoop">
                  <Switch />
                </Form.Item>
              </div>
            ) : null)}
        </Form>
      </EditCompressorFormContainer>
    );
  },
);

export default EditCompressorForm;
